import {
  Label,
  FormField,
  FormItem,
  FormControl,
  FormMessage,
  Input,
  Typography,
  InputPassword,
  RadioGroup,
  RadioGroupItem,
  Stack,
} from "@altimateai/lego";
import { Control, useWatch } from "react-hook-form";
import { DatabricksAuthType, DatabricksAuthTypeValue } from "@/pages/datastores/databricksTypes";
import type { DatabricksFormData } from "./index";

interface DatabricksFormFieldsProps {
  control: Control<DatabricksFormData>;
}

const DatabricksFormFields = ({ control }: DatabricksFormFieldsProps): JSX.Element => {
  const authType = useWatch({ control, name: "auth_type" }) as DatabricksAuthTypeValue | null;

  return (
    <>
      <Stack direction="row" gap="lg">
        <FormField
          control={control}
          name="name"
          render={({ field }) => (
            <FormItem className="al-flex-1">
              <Label htmlFor="name">Connection Name</Label>
              <FormControl>
                <Input id="name" placeholder="My Databricks Connection" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="description"
          render={({ field }) => (
            <FormItem className="al-flex-1">
              <Label htmlFor="description">Description (optional)</Label>
              <FormControl>
                <Input id="description" placeholder="Production SQL Warehouse" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </Stack>

      <FormField
        control={control}
        name="server_hostname"
        render={({ field }) => (
          <FormItem>
            <Label htmlFor="server_hostname">Server Hostname</Label>
            <FormControl>
              <Input
                id="server_hostname"
                placeholder="dbc-xxxxxxxx-xxxx.cloud.databricks.com"
                {...field}
              />
            </FormControl>
            <FormMessage />
            <Typography size="xs" className="al-text-muted-foreground">
              Found in SQL Warehouse connection details under &quot;Server Hostname&quot;
            </Typography>
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="http_path"
        render={({ field }) => (
          <FormItem>
            <Label htmlFor="http_path">HTTP Path</Label>
            <FormControl>
              <Input id="http_path" placeholder="/sql/1.0/warehouses/xxxxxxxxxxxxxxxx" {...field} />
            </FormControl>
            <FormMessage />
            <Typography size="xs" className="al-text-muted-foreground">
              Found in SQL Warehouse connection details under &quot;HTTP Path&quot;
            </Typography>
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="auth_type"
        render={({ field }) => (
          <FormItem>
            <Label>Authentication Method</Label>
            <FormControl>
              <RadioGroup onValueChange={field.onChange} value={field.value}>
                <Stack direction="row" gap="lg">
                  <Stack direction="row" align="center" gap="sm">
                    <RadioGroupItem
                      value={DatabricksAuthType.PAT}
                      id="auth_pat"
                      aria-labelledby="auth_pat_label"
                    />
                    <Label
                      htmlFor="auth_pat"
                      id="auth_pat_label"
                      className="al-font-normal al-cursor-pointer"
                    >
                      Personal Access Token (PAT)
                    </Label>
                  </Stack>
                  <Stack direction="row" align="center" gap="sm">
                    <RadioGroupItem
                      value={DatabricksAuthType.OAUTH_M2M}
                      id="auth_oauth"
                      aria-labelledby="auth_oauth_label"
                    />
                    <Label
                      htmlFor="auth_oauth"
                      id="auth_oauth_label"
                      className="al-font-normal al-cursor-pointer"
                    >
                      OAuth M2M (Service Principal)
                    </Label>
                  </Stack>
                  <Stack direction="row" align="center" gap="sm">
                    <RadioGroupItem
                      value={DatabricksAuthType.AZURE_SP}
                      id="auth_azure_sp"
                      aria-labelledby="auth_azure_sp_label"
                    />
                    <Label
                      htmlFor="auth_azure_sp"
                      id="auth_azure_sp_label"
                      className="al-font-normal al-cursor-pointer"
                    >
                      Azure Service Principal (Entra ID)
                    </Label>
                  </Stack>
                </Stack>
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {authType === DatabricksAuthType.PAT && (
        <FormField
          control={control}
          name="access_token"
          render={({ field }) => (
            <FormItem>
              <Label htmlFor="access_token">Personal Access Token</Label>
              <FormControl>
                <InputPassword
                  id="access_token"
                  placeholder="dapi..."
                  autoComplete="new-password"
                  {...field}
                />
              </FormControl>
              <FormMessage />
              <Typography size="xs" className="al-text-muted-foreground">
                Create a PAT in User Settings &rarr; Developer &rarr; Access tokens
              </Typography>
            </FormItem>
          )}
        />
      )}

      {authType === DatabricksAuthType.AZURE_SP && (
        <FormField
          control={control}
          name="azure_tenant_id"
          render={({ field }) => (
            <FormItem>
              <Label htmlFor="azure_tenant_id">Tenant ID</Label>
              <FormControl>
                <Input
                  id="azure_tenant_id"
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  {...field}
                />
              </FormControl>
              <FormMessage />
              <Typography size="xs" className="al-text-muted-foreground">
                The Microsoft Entra ID Directory (tenant) ID of your app registration
              </Typography>
            </FormItem>
          )}
        />
      )}

      {(authType === DatabricksAuthType.OAUTH_M2M || authType === DatabricksAuthType.AZURE_SP) && (
        <>
          <FormField
            control={control}
            name="client_id"
            render={({ field }) => (
              <FormItem>
                <Label htmlFor="client_id">Client ID</Label>
                <FormControl>
                  <Input
                    id="client_id"
                    placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
                <Typography size="xs" className="al-text-muted-foreground">
                  The Application (client) ID of your Service Principal
                </Typography>
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="client_secret"
            render={({ field }) => (
              <FormItem>
                <Label htmlFor="client_secret">Client Secret</Label>
                <FormControl>
                  <InputPassword
                    id="client_secret"
                    placeholder="Enter client secret"
                    autoComplete="new-password"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
                <Typography size="xs" className="al-text-muted-foreground">
                  The secret created for your Service Principal
                </Typography>
              </FormItem>
            )}
          />
        </>
      )}
    </>
  );
};

export default DatabricksFormFields;
